import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';

import {AssetService} from '@app/asset';
import {deserialize} from '@app/serializable';
import {StorageService} from '@app/shared/storage.service';
import {User} from '@app/shared/user.service';
import {StateService} from '@app/state/state.service';
// [AUTH Overwrite]
import {AuthService as Auth0Service} from '@auth0/auth0-angular';

import {Observable, of, ReplaySubject} from 'rxjs';
import {catchError, filter, map, mergeMap, tap} from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    public user: ReplaySubject<User>;
    private _user: User;

    constructor(
        private storageService: StorageService,
        private assetService: AssetService,
        private stateService: StateService,
        // [AUTH Overwrite]
        private auth0Service: Auth0Service,
        private http: HttpClient,
    ) {
        this.user = new ReplaySubject<User>();
        this._user = null;
    }

    public isAuthenticated(): Observable<boolean> {
        const token = this.storageService.get('token');
        if (token) {
            if (this._user !== null) {
                this.user.next(this._user);
                return of(true);
            }

            return this.login(token).pipe(map((user) => user !== null));
        }

        return of(false);
    }

    // [AUTH Overwrite]
    redirectToIdp() {
        return this.auth0Service.loginWithRedirect({
            appState: { target: '/get-started/landing' },
        });
    }

    // [AUTH Overwrite]
    getIdpToken(): Observable<string> {
        return this.auth0Service.idTokenClaims$.pipe(map((rawToken) => rawToken.__raw));
    }

    login(token: string, provider: string = null): Observable<User> {
        const options = {
            provider,
            withCredentials: true,
        };
        if (token) {
            // This will be used by the interceptor for the backend to use
            this.storageService.set('token', token);
        }
        if (provider) {
            this.storageService.set('provider', provider);
        }

        return this.http.post('/api/auth/login/', options).pipe(
            catchError((error) => {
                if (error.status === 401 || error.status === 403) {
                    // Session or token expired, just log them out and return to login
                    return this.logout().pipe(
                        mergeMap(() => this.redirectToIdp())
                    );
                }
                return of(null);
            }),
            filter((data) => Boolean(data)),
            map((data) => {
                return deserialize(User, data);
            }),
            tap((user) => {
                this._user = user;
                this.user.next(this._user);
            }),
        );
    }

    logout() {
        const options = {
            withCredentials: true,
        };
        return this.http.post('/api/auth/logout/', options)
            .pipe(
                mergeMap(() => {
                    this.storageService.pop('token');
                    this.assetService.clear();
                    this.stateService.clear();
                    this._user = null;
                    // [AUTH Overwrite]
                    return this.auth0Service.logout({
                        logoutParams: {
                            returnTo: window.document.location.origin,
                        },
                    });
                }),
            );
    }
}
